import React, {
  useState,
  useContext,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { NotificationsContext } from "../ui-components/Notifications";
import { useIntl, FormattedMessage } from "react-intl";
import apiRequest from "../services/apiRequest";
import useCountryAxiosInstance from "../hooks/useCountryAxiosInstance";

import Input from "../ui-components/Input";
import Select from "../ui-components/Select";
import Button from "../ui-components/Button";
import Toggle from "../ui-components/Toggle";
import ChosePassword from "../ui-components/forms/ChosePassword";
import CheckboxGroup from "../ui-components/CheckboxGroup";
import CountryContext from "../CountryContext";
import activeCountries from "../activeCountries";
import { validateEmail } from "@utils/validate-email";

import { INTEREST_OPTIONS } from "../utils/constants";
import UserTypeSelect from "../components/UserTypeSelect";
import {Link, useParams} from "react-router-dom";
import {CITY_REGEX, ZIP_CODE_REGEX} from "../utils/regex";
import checkIfEmailDomainExists from "../utils/checkIfEmailDomainExists";
import Modal from "../ui-components/Modal";
import {ExclamationTriangleIcon} from "@heroicons/react/24/outline";
import {PublicPage} from "@components/Public";
import {ArrowLeftIcon} from "@ui-components/icons/ArrowLeftIcon";
import Spinner from "@ui-components/Spinner";
import Checkbox from "@ui-components/Checkbox";
import NotFound from "@ui-components/pages/NotFound";
import Label from "../ui-components/Label";


export default function Signup() {
  const intl = useIntl();
  const { country } = useContext(CountryContext);
  const { push } = useContext(NotificationsContext);
  const { encodedUserData } = useParams();
  const initData = encodedUserData ? JSON.parse(atob(encodedUserData)) : {};
  const accountUserRole = initData.account_user_role;
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [has_revit_plugin] = useState(initData.has_revit_plugin || false);
  const [selectedCountry, setSelectedCountry] = useState(country.code);
  const countryOptions = activeCountries.map((i) => ({
    value: i.code,
    name: i.name,
  }));
  const [city, setCity] = useState(initData.city || "");
  const [zipCode, setZipCode] = useState(initData.zip_code || "");
  const [email, setEmail] = useState(initData.email || "");
  const [password, setPassword] = useState("");
  const [validPassword, setValidPassword] = useState(false);
  const [firstName, setFirstName] = useState(initData.firstname || "");
  const [lastName, setLastName] = useState(initData.lastname || "");
  const [company, setCompany] = useState(initData.company || "");
  const [vat, setVat] = useState(initData.vat || "");
  const [flgMarketing, setFlgMarketing] = useState(false);
  const [role, setRole] = useState(initData.role || "");
  const [interest, setInterest] = useState([]);
  const [sector, setSector] = useState(" ");
  const [qualification, setQualification] = useState(" ");
  const [emailDomainValidationError, setEmailDomainValidationError] = useState(null);
  const [zipCodeError, setZipCodeError] = useState(false);

  const [emailSent, setEmailSent] = useState(false);
  const [externalRoles, setExternalRoles] = useState(false);
  const apiRequestCountry = useCountryAxiosInstance();

  const formRef = useRef(null);

  const isResale = ["resale_tintometer", "resale"]
    .map((name) => `user_type_${name}`)
    .includes(accountUserRole || role);

  /*
  userAlreadyExists is true if the user has an id (it means there is a placeholder user
  who's flg_active is false) and we need to send an invitation to the user to make the
  registration complete. (resales/suggested users)
   */
  const userAlreadyExists = !!initData.id
  /*
  ask for zip code only if user has no id (new user from share worksite) or
  user has no role_id (new user from suggested user invitation)
  */
  const askForZipCode = !initData.id || !initData.role_id || initData.role === 'user_type_contact_user';

  const sectorOptions = {
    user_type_design_studio: [
      " ",
      "architecture",
      "engineering",
      "interior_design",
      "other",
    ].map((i) => ({ value: i, name: intl.formatMessage({ id: i }) })),
    user_type_enterprise: [
      " ",
      "structural_reinforcement",
      "waterproofing",
      "vertical_works",
      "construction",
      "civil_works_specialist",
    ].map((i) => ({ value: i, name: intl.formatMessage({ id: i }) })),
  };

  const qualificationOptions = {
    user_type_design_studio: [
      " ",
      "engineer",
      "architect",
      "owner",
      "surveyor",
      "decoration/interior_design",
      "other",
    ].map((i) => ({ value: i, name: intl.formatMessage({ id: i }) })),
    user_type_enterprise: [
      " ",
      "engineer",
      "architect",
      "owner",
      "surveyor",
      "applicator",
      "other",
    ].map((i) => ({ value: i, name: intl.formatMessage({ id: i }) })),
    user_type_resale_tintometer: [
      " ",
      "owner",
      "other",
    ].map((i) => ({ value: i, name: intl.formatMessage({ id: i }) })),
    user_type_resale: [
      " ",
      "owner",
      "other",
    ].map((i) => ({ value: i, name: intl.formatMessage({ id: i }) })),
  };

  const _onSubmit = useCallback(
    async (e) => {
      try {
        e.preventDefault();

        if (!city || !CITY_REGEX.test(city)) {
          push({
            title: intl.formatMessage({ id: "invalid_city" }),
            type: "error",
          });
          return;
        }
        if (!zipCode || !ZIP_CODE_REGEX.test(zipCode)) {
          push({
            title: intl.formatMessage({ id: "invalid_zip_code" }),
            type: "error",
          });
          return;
        }
        if (!role) {
          push({
            title: intl.formatMessage({ id: "need_to_select_a_role" }),
            type: "error",
          });
          return;
        }
        if (
          ["user_type_design_studio", "user_type_enterprise"].includes(role) &&
          (!company || sector === " " || qualification === " ")
        ) {
          push({
            title: intl.formatMessage({ id: "enter_company_data" }),
            type: "error",
          });
          return;
        }
        if (["user_type_freelance"].includes(role) && sector === " ") {
          push({
            title: intl.formatMessage({ id: "enter_specialization" }),
            type: "error",
          });
          return;
        }
        if (!email || !firstName || !lastName || emailDomainValidationError || !validateEmail(email)) {
          push({
            title: intl.formatMessage({ id: "enter_name_or_email" }),
            type: "error",
          });
          return;
        }
        if (!password) {
          push({
            title: intl.formatMessage({ id: "enter_valid_password" }),
            type: "error",
          });
          return;
        }
        if (password.includes(" ") || password.length < 8) {
          return;
        }

        setLoading(true);

        const postData = {
          email,
          flg_internal: false,
          flg_active: false,
          password,
          firstname: firstName,
          lastname: lastName,
          countries: selectedCountry,
          has_revit_plugin,
          flg_gdpr: true,
          flg_mktg: flgMarketing,
          role: role.replace("user_type_", ""),
          city,
          zip_code: zipCode,
          company: company || undefined,
          vat: vat || undefined,
          interest: interest.length > 0 ? interest.join(";") : undefined,
          stakeholder: [
            "user_type_design_studio",
            "user_type_enterprise",
            "user_type_freelance",
          ].includes(role)
            ? sector
            : undefined,
          qualification: [
            "user_type_design_studio",
            "user_type_enterprise",
          ].includes(role)
            ? qualification
            : undefined,
          // If data is prepopulated (and locked), we want to skip the zip code check performed by the backend.
          // This is because the zip code passed by Kerakoll might be incorrect, and we don't want to block the user
          // from signing up.
          ignore_zip_code_warning: !askForZipCode,
          by_invitation: userAlreadyExists,
        };
        await apiRequest.post("/users/signup", postData, {publicRequest: true});
        push({
          title: intl.formatMessage({ id: "sign_up_ok" }),
          type: "success",
        });
        setEmailSent(true);
      } catch (error) {
        if (error.response.status === 304) {  // not modified (there was a zip code mismatch)
          setZipCodeError(true);
        }
        else {
          let msg;
          switch (error.response.data.detail) {
            case "error_localization":
              msg = "cannot_localize_user";
              break;
            default:
              msg = error.response.data.detail || "server_error";
              break;
          }
          push({
            title: intl.formatMessage({ id: msg }),
            type: "error",
          });
        }
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line
    [
      city,
      zipCode,
      email,
      firstName,
      lastName,
      role,
      password,
      flgMarketing,
      sector,
      qualification,
      push,
    ]
  );

  useEffect(() => {
    let cancel = false;
    const fetchUserTypes = async () => {
      try {
        const [allUserTypes] = await Promise.all([
          apiRequestCountry.get(`/user_types/all`, { publicRequest: true }),
        ]);
        if (!cancel) {
          setExternalRoles(allUserTypes);
        }
        if (initData.role_id) {
          const defaultRole = allUserTypes.find((ut) => ut.associated_roles_ids?.includes(initData.role_id));
          if (defaultRole)
            setRole(defaultRole.cod_user_type);
        }
      } catch (error) {
        if (!cancel) {
          console.log("errore", error);
        }
      }
    };

    fetchUserTypes();
    // eslint-disable-next-line
  }, []);

  if (country.code !== "it")
    return <NotFound />;

  let content;
  if (!externalRoles) {
    // CASE A: loading needed information --> show spinner
    content = <Spinner />;
  }
  else if (emailSent) {
    // CASE B: user completed registration and has been sent a configuration mail --> show confirmation message
    content = (
      <div className="flex flex-col gap-3 text-center">
        <p className="font-semibold text-xl">
          <FormattedMessage
            id="almost_there"
            defaultMessage="Ci siamo quasi!"
          />
        </p>
        <p>
          <FormattedMessage id="check_your_email"/>
        </p>
      </div>
    );
  } else {
    content = (
      <div className="flex flex-col md:w-2/3 xl:w-1/2">
        <div className="flex flex-col gap-4 md:py-14">
          {/* ----- Link to login page -----*/}
          <Link
            to={`/${country.code}/login`}
            className="font-semibold text-black hover:text-black flex flex-row items-center gap-2 text-base leading-none"
          >
            <ArrowLeftIcon className="w-4"/>
            <FormattedMessage
              id="already-have-account-back-to-login"
              defaultMessage="Hai già un account? Accedi"
            />
          </Link>
          {/* ----- Signup form -----*/}
          <form action="#" method="POST" onSubmit={_onSubmit} ref={formRef}>
            {/* ----- User type selection -----*/}
            {!['user_type_resale', 'user_type_resale_tintometer'].includes(
              accountUserRole
            ) && <div className="my-7">
              <p>
                <FormattedMessage id="select_user_type"/>
              </p>
              <div className="my-4">
                <UserTypeSelect
                  value={role}
                  onClick={setRole}
                  userTypes={externalRoles
                    .filter((x) => x.active || !!x.associated_roles_ids?.includes(initData.role_id))
                    .sort((a, b) => a.order - b.order)}
                  disableUnselected={!!initData.role_id}
                />
              </div>
            </div>}
            {!!role && <>
              <div className="grid grid-cols-1 md:grid-cols-6 gap-x-6 gap-y-1">
                <div className="md:col-span-2 mb-2">
                  <Select
                    label={intl.formatMessage({id: "country"})}
                    options={countryOptions}
                    value={selectedCountry}
                    onChange={(e) => {
                      setSelectedCountry(e.target.value);
                    }}
                    disabled={!!initData.country}
                  />
                </div>
                <div className="md:col-span-2">
                  <Input
                    value={city}
                    onChange={(e) => {
                      setCity(e.target.value);
                    }}
                    label={intl.formatMessage({id: "city"})}
                    type="text"
                    errorMessage={intl.formatMessage({id: "invalid_city"})}
                    validation={(value) => CITY_REGEX.test(value)}
                    disabled={!!initData.city && role !== 'user_type_contact_user'}
                    required
                  />
                </div>
                {/*
                  If data is prepopulated (and locked), we want to hide the zip code field from the user.
                  This is because the zip code passed by Kerakoll might be incorrect, and we don't want to confuse the user.
                */}
                <div className={`md:col-span-2 ${!askForZipCode ? "invisible" : ""}`}>
                  <Input
                    value={zipCode}
                    onChange={(e) => {
                      setZipCode(e.target.value);
                    }}
                    label={intl.formatMessage({id: "zip_code"})}
                    type="text"
                    errorMessage={intl.formatMessage({id: "invalid_zip_code"})}
                    validation={(value) => ZIP_CODE_REGEX.test(value)}
                    disabled={!!initData.zip_code && role !== 'user_type_contact_user'}
                    required
                  />
                </div>
                {[
                  "user_type_design_studio",
                  "user_type_enterprise",
                  "user_type_resale_tintometer",
                  "user_type_resale",
                  "user_type_contact_user",
                ].includes(role) && <>
                  <div className={isResale ? "md:col-span-3" : "md:col-span-2"}>
                    <Input
                      value={role === 'user_type_contact_user'? initData.account_user_company: company}
                      onChange={(e) => {
                        setCompany(e.target.value);
                      }}
                      label={intl.formatMessage({id: "company_name"})}
                      type="text"
                      errorMessage={intl.formatMessage({
                        id: "enter_company_name",
                      })}
                      disabled={!!initData.company || role === 'user_type_contact_user'}
                      required
                    />
                  </div>
                  <div className={isResale ? "md:col-span-3" : "md:col-span-2"}>
                    <Input
                      value={role === 'user_type_contact_user'? initData.account_user_vat: vat}
                      onChange={(e) => {
                        setVat(e.target.value);
                      }}
                      label={intl.formatMessage({id: "vat"})}
                      type="text"
                      errorMessage={intl.formatMessage({id: "enter_vat"})}
                      disabled={!!initData.vat || role === 'user_type_contact_user'}
                      required
                    />
                  </div>
                  {
                    !isResale &&  (
                      <div className="md:col-span-2">
                        <Select
                          label={intl.formatMessage({id: "sector"})}
                          options={sectorOptions[role === 'user_type_contact_user'? initData.account_user_role: role]}
                          value={role === 'user_type_contact_user'? initData.account_user_sector: sector}
                          onChange={(e) => {
                            setSector(e.target.value);
                          }}
                          disabled={role === 'user_type_contact_user'}
                        />
                      </div>
                    )
                  }
                </>}
                <div className="md:col-span-2">
                  <Input
                    value={firstName}
                    onChange={(e) => {
                      setFirstName(e.target.value);
                    }}
                    label={intl.formatMessage({id: "name"})}
                    type="text"
                    errorMessage={intl.formatMessage({id: "enter_name"})}
                    required
                  />
                </div>
                <div className="md:col-span-2">
                  <Input
                    value={lastName}
                    onChange={(e) => {
                      setLastName(e.target.value);
                    }}
                    label={intl.formatMessage({id: "surname"})}
                    type="text"
                    errorMessage={intl.formatMessage({id: "enter_surname"})}
                    required
                  />
                </div>
                {role === "user_type_freelance" && (
                  <div className="md:col-span-2">
                    <Select
                      label={intl.formatMessage({id: "specialization"})}
                      options={sectorOptions["user_type_design_studio"]}
                      value={sector}
                      onChange={(e) => {
                        setSector(e.target.value);
                      }}
                    />
                  </div>
                )}
                {[
                  "user_type_design_studio",
                  "user_type_enterprise",
                  "user_type_resale_tintometer",
                  "user_type_resale",
                  "user_type_contact_user",
                ].includes(role) && (
                  <div className="md:col-span-2">
                    <Select
                      label={intl.formatMessage({id: "role"})}
                      options={qualificationOptions[
                        role === 'user_type_contact_user' ? initData.account_user_role : role
                        ]}
                      value={qualification}
                      onChange={(e) => {
                        setQualification(e.target.value);
                      }}
                    />
                  </div>
                )}
                {["user_type_private"].includes(role) && (
                  <div className="mt-4 mb-6">
                    <CheckboxGroup
                      labelClasses="font-semibold"
                      horizontal
                      title={intl.formatMessage({id: "your_interests"})}
                      options={INTEREST_OPTIONS.map((i) => ({
                        name: intl.formatMessage({id: i}),
                        value: i,
                      }))}
                      values={interest}
                      onChange={(value) => {
                        if (interest.indexOf(value) >= 0) {
                          setInterest([...interest.filter((v) => v !== value)]);
                        } else {
                          setInterest([...interest, value]);
                        }
                      }}
                    />
                  </div>
                )}
                <div className="md:col-span-3">
                  <Input
                    value={email}
                    onChange={async (e) => {
                      setEmail(e.target.value);
                      const domainExists = await checkIfEmailDomainExists(e.target.value, apiRequestCountry);
                      setEmailDomainValidationError(domainExists !== null ? !domainExists : null);
                    }}
                    label="Email"
                    type="email"
                    errorMessage={intl.formatMessage({
                      id: emailDomainValidationError === true
                        ? "unrecognized_email_domain"
                        : "enter_email"
                    })}
                    validation={() => !(!email || emailDomainValidationError === true || !validateEmail(email))}
                    disabled={!!initData.email}
                    required
                  />
                </div>
                <div className="md:col-span-3">
                  <ChosePassword
                    password={password}
                    setPassword={setPassword}
                    showPassword={showPassword}
                    setValidPassword={setValidPassword}
                  />
                  <div className="flex items-center">
                    <Checkbox
                      id="show-pswd"
                      name="show-pswd"
                      checked={showPassword}
                      onChange={(e) => {
                        setShowPassword(e.target.checked);
                      }}
                    />
                    <label className="ml-2 block text-black-500">
                      <FormattedMessage id="show_password"/>
                    </label>
                  </div>
                </div>
              </div>
              <div className="mt-8 mb-6">
                <div className="pr-16 mb-2">
                  <Label>
                    <FormattedMessage
                      id="consent_gdpr_alt"
                      defaultMessage="Autorizzo Kerakoll a trattare i miei dati personali per fini di registrazione, come indicato nel punto B) dell’{link}."
                      values={{
                        link: (
                          <Link
                            to={{pathname: "/privacy-policy", hash: "#puntoB"}}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-black-500 font-semibold underline"
                          >
                            <span className="lowercase">
                              <FormattedMessage id="privacy_info_alt" defaultMessage="Informativa"/>
                            </span>
                          </Link>
                        ),
                      }}
                    />
                  </Label>
                </div>
                <Toggle
                  vertical
                  label={intl.formatMessage({
                    id: "consent_marketing_cta_alt",
                    defaultMessage: "Desidero restare aggiornato con tutte le novità del mondo Kerakoll come indicato nel punto C) dell’{link} per finalità di invio di comunicazioni di natura promozionale aventi ad oggetto attività e prodotti di Kerakoll, anche tramite modalità automatizzate (sms, mms, fax, e-mail) e contatti telefonici, di comunicazioni. Dichiaro di avere compiuto almeno 16 anni.",
                  }, {
                    link: (
                      <Link
                        to={{pathname: "/privacy-policy", hash: "#puntoC"}}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-black-500 font-semibold underline"
                      >
                        <span className="lowercase">
                          <FormattedMessage id="privacy_info_alt" defaultMessage="Informativa"/>
                        </span>
                      </Link>
                    ),
                  })}
                  checked={flgMarketing}
                  onChange={() => setFlgMarketing(!flgMarketing)}
                  marginCls=""
                  labelCls=""
                  flexAlignCls="items-center justify-between gap-3"
                />
              </div>
              <div className="mt-8">
                <Button
                  full
                  type="submit"
                  submitting={loading}
                  uppercase={false}
                  disabled={!validPassword}
                >
                  <FormattedMessage
                    id="sign_up_cta"
                    defaultMessage="Registrati"
                  />
                </Button>
              </div>
            </>}
          </form>
        </div>
      </div>
    )
  }

  return <>
    <PublicPage showCountrySelector={false} showDeveloperLoginButton={false}>
      <div className="flex-1 flex items-center justify-center bg-white px-4 sm:px-8 py-10">
        {content}
      </div>
    </PublicPage>
    {
      zipCodeError && !emailSent && (
        <Modal
          type="warning"
          onExit={() => { setZipCodeError(false); }}
        >
          <div className="w-16 h-16 p-3 bg-yellow-100 text-yellow-500 rounded-full mb-3 mx-auto">
            <ExclamationTriangleIcon />
          </div>
          <h1 className="text-center text-xl">Attenzione</h1>
          <p className="my-4">
            <FormattedMessage id="zip_code_error_description" />
          </p>
          <div className="mt-6">
            <Button
              full
              onClick={() => { setZipCodeError(false); }}
            >
              <FormattedMessage id="ok_i_understood" />
            </Button>
          </div>
        </Modal>
      )
    }
  </>
}
